/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useState, useEffect } from 'react';
import moment from 'moment';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Autocomplete,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { toast } from 'react-toastify';
import { DatePicker } from '@mui/x-date-pickers';
import AppConst from '../AppLayout/const';
import {
  getFilterAgenda,
  getFilterSpecialty,
  getCallVolumeVsProductUsageReport,
} from '../../api/weeklyProductReportApi';

export default function CallVolumeVsProductUsageReportModal() {
  const { toggleModal, onToggleModal } = useContext(AppConst.AppContext);
  const [toggle, setToggle] = useState(false);

  const defaultFilter = {
    agenda: [{ label: 'ALL', value: null }],
    specialty: [{ label: 'ALL', value: null }],
  };

  const [fromValue, setFromValue] = useState(moment().format('YYYY-MM-DD'));
  const [toValue, setToValue] = useState(moment().format('YYYY-MM-DD'));
  const [agendaOptions, setAgendaOptions] = useState([]);
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [filterForm, setFilterForm] = useState(defaultFilter);

  const loadAgendaOptions = async () => {
    const request = {
      keyword: '',
    };
    const response = await getFilterAgenda(request);
    const { code, data } = response.data;
    if (code === '200') {
      const dropdownValue = data?.map((row) => ({
        value: row.description,
        label: row.description,
      }));

      setAgendaOptions([{ label: 'ALL', value: null }, ...dropdownValue]);

      if (dropdownValue.length < 2) {
        setFilterForm({ ...filterForm, agenda: [dropdownValue[0]] });
      } else {
        setFilterForm({ ...filterForm, agenda: [{ label: 'ALL', value: null }] });
      }
    }
  };

  const loadSpecialtyOptions = async () => {
    const response = await getFilterSpecialty();
    const { code, data } = response.data;
    if (code === '200') {
      const dropdownValue = data?.map((row) => ({
        value: row.specialty,
        label: row.specialty,
      }));

      setSpecialtyOptions([{ label: 'ALL', value: null }, ...dropdownValue]);

      if (dropdownValue.length < 2) {
        setFilterForm({ ...filterForm, specialty: [dropdownValue[0]] });
      } else {
        setFilterForm({ ...filterForm, specialty: [{ label: 'ALL', value: null }] });
      }
    }
  };

  useEffect(() => {
    if (toggleModal?.url === 'report/call_volume_vs_product_usage_report') {
      setToggle(true);

      loadAgendaOptions();
      loadSpecialtyOptions();
    }
  }, [toggleModal]);

  const handleExport = async () => {
    try {
      toast.loading('Please wait ...');
      const res = await getCallVolumeVsProductUsageReport({
        from: fromValue,
        to: toValue,
        agenda:
          filterForm?.agenda.filter((item) => item.value === null).length > 0
            ? null
            : filterForm?.agenda.map((row) => row.value).sort(),
        specialty:
          filterForm?.specialty.filter((item) => item.value === null).length > 0
            ? null
            : filterForm?.specialty.map((row) => row.value).sort(),
      });

      const { code, result, message } = res.data.response;
      if (code !== '200') {
        throw Object.assign(new Error(message), { code: 402 });
      }
      toast.dismiss();

      window.location.href = result;
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    }
  };

  const handleSubmit = () => {
    handleExport(fromValue, toValue);
  };

  const handleChangeFrom = (value) => {
    setFromValue(moment(value).format('YYYY-MM-DD'));
  };

  const handleChangeTo = (value) => {
    setToValue(moment(value).format('YYYY-MM-DD'));
  };

  const handleCancel = () => {
    onToggleModal(null);
    setToggle(false);
  };

  return (
    <Dialog open={toggle} onClose={handleCancel} maxWidth='sm' fullWidth>
      <DialogTitle>Call Volume vs Product Usage Report</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} mb={1}>
          <Grid md={12}>
            <Autocomplete
              id='agenda'
              multiple
              fullWidth
              options={agendaOptions}
              autoComplete
              includeInputInList
              filterSelectedOptions
              getOptionLabel={(option) => (option.label ? option.label : '')}
              value={Array.isArray(filterForm.agenda) ? filterForm.agenda : []}
              noOptionsText='No Agendas'
              onChange={(e, newValue) => {
                let updatedNewValue = newValue;

                if (newValue.length === 0) {
                  // if remove all month option set default to all
                  updatedNewValue = [agendaOptions[0]];
                } else if (newValue[newValue.length - 1].value === null) {
                  // if last selected in ALL, remove all other options and retain ALL
                  updatedNewValue = [agendaOptions[0]];
                } else if (newValue.length !== 1) {
                  // if selected other months, remove ALL in selected options
                  updatedNewValue = newValue.filter(
                    (option) => option.value !== agendaOptions[0].value,
                  );
                }

                setFilterForm({
                  ...filterForm,
                  agenda: updatedNewValue,
                });
              }}
              isOptionEqualToValue={(option, selected) => option.value === selected.value}
              renderInput={(params) => <TextField {...params} label='Agenda' />}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              size='small'
            />
          </Grid>
          <Grid md={12}>
            <Autocomplete
              id='specialty'
              multiple
              fullWidth
              options={specialtyOptions}
              autoComplete
              includeInputInList
              filterSelectedOptions
              getOptionLabel={(option) => (option.label ? option.label : '')}
              value={Array.isArray(filterForm.specialty) ? filterForm.specialty : []}
              noOptionsText='No Specialties'
              onChange={(event, newValue) => {
                let updatedNewValue = newValue;

                if (newValue.length === 0) {
                  // if remove all month option set default to all
                  updatedNewValue = [specialtyOptions[0]];
                } else if (newValue[newValue.length - 1].value === null) {
                  // if last selected in ALL, remove all other options and retain ALL
                  updatedNewValue = [specialtyOptions[0]];
                } else if (newValue.length !== 1) {
                  // if selected other months, remove ALL in selected options
                  updatedNewValue = newValue.filter(
                    (option) => option.value !== specialtyOptions[0].value,
                  );
                }

                setFilterForm({
                  ...filterForm,
                  specialty: updatedNewValue,
                });
              }}
              isOptionEqualToValue={(option, selected) => option.value === selected.value}
              renderInput={(params) => <TextField {...params} label='Specialty' fullWidth />}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              size='small'
            />
          </Grid>
          <Grid xs={6}>
            <DatePicker
              label='from'
              views={['month', 'day', 'year']}
              value={fromValue}
              // eslint-disable-next-line react/jsx-props-no-spreading
              renderInput={(params) => (
                <TextField {...params} label='From' size='small' fullWidth />
              )}
              onChange={(value) => handleChangeFrom(value)}
            />
          </Grid>
          <Grid xs={6}>
            <DatePicker
              label='to'
              views={['month', 'day', 'year']}
              value={toValue}
              // eslint-disable-next-line react/jsx-props-no-spreading
              renderInput={(params) => <TextField {...params} label='To' size='small' fullWidth />}
              onChange={(value) => handleChangeTo(value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit}>Submit</Button>
        <Button onClick={handleCancel}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
